import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, WHITE } from '../lib/ui';
import { Avatar, Btn, Input, Select, Field, Card, Empty } from '../lib/ui';

const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

export default function ClientsView({ matters, onMatterUpdate, isAdmin }) {
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('list'); // 'list' | 'create'
  const [form,     setForm]     = useState({ name:'', email:'', password:'', matter_id:'' });
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [editId,   setEditId]   = useState(null);
  const [editEmail,setEditEmail]= useState('');

  // Load clients — matters that have a client_email set
  const loadClients = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('matters')
      .select('id, name, client, client_email, status, deadline')
      .not('client_email', 'is', null)
      .order('created_at', { ascending: false });
    setClients(data || []);
    setLoading(false);
  };

  useEffect(() => { loadClients(); }, []);

  const unlinkedMatters = matters.filter(m => !m.client_email);

  const valid = form.name.trim() && form.email.trim() && form.password.length >= 6 && form.matter_id;

  const handleCreate = async () => {
    if (!valid) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      // 1. Create Supabase auth account
      const { error: authErr } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: { data: { full_name: form.name } }
      });
      if (authErr) throw authErr;

      // 2. Link email to the matter
      const { error: mErr } = await supabase
        .from('matters')
        .update({ client_email: form.email, client: form.name })
        .eq('id', form.matter_id);
      if (mErr) throw mErr;

      // Refresh local matters if callback provided
      if (onMatterUpdate) onMatterUpdate(form.matter_id, { client_email: form.email, client: form.name });

      setSuccess(`Client account created for ${form.name} (${form.email}). Share the password with them securely.`);
      setForm({ name:'', email:'', password:'', matter_id:'' });
      loadClients();
    } catch (e) {
      setError(e.message || 'Failed to create client account.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmail = async (matterId) => {
    if (!editEmail.trim()) return;
    const { error } = await supabase.from('matters').update({ client_email: editEmail }).eq('id', matterId);
    if (error) { alert(error.message); return; }
    if (onMatterUpdate) onMatterUpdate(matterId, { client_email: editEmail });
    setEditId(null); setEditEmail('');
    loadClients();
  };

  const handleRemoveAccess = async (matterId) => {
    if (!window.confirm('Remove client portal access for this matter? Their login account will still exist but they won\'t see any matter when they log in.')) return;
    const { error } = await supabase.from('matters').update({ client_email: null }).eq('id', matterId);
    if (error) { alert(error.message); return; }
    if (onMatterUpdate) onMatterUpdate(matterId, { client_email: null });
    loadClients();
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Header tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:'1px solid #E5E0D8' }}>
        {[
          { key:'list',   label:`Active Client Accounts (${clients.length})` },
          ...(isAdmin ? [{ key:'create', label:'+ Create New Client Account' }] : []),
        ].map(tb => (
          <button key={tb.key} onClick={()=>{ setTab(tb.key); setError(''); setSuccess(''); }} style={{
            padding:'10px 22px', border:'none', background:'none', cursor:'pointer', fontFamily:'inherit',
            fontSize:13, fontWeight:tab===tb.key?700:400, color:tab===tb.key?NAVY:'#888',
            borderBottom:tab===tb.key?`2px solid ${GOLD}`:'2px solid transparent',
          }}>{tb.label}</button>
        ))}
      </div>

      {/* Client list */}
      {tab==='list' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {loading && <div style={{ color:'#BBB', fontSize:13, padding:'20px 0', textAlign:'center' }}>Loading…</div>}
          {!loading && clients.length===0 && (
            <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:'32px', textAlign:'center' }}>
              <div style={{ fontSize:32, marginBottom:12 }}>👤</div>
              <div style={{ fontSize:14, fontWeight:600, color:NAVY, marginBottom:6 }}>No client accounts yet</div>
              <div style={{ fontSize:13, color:'#888', marginBottom:16 }}>Create your first client account to give them portal access.</div>
              <Btn onClick={()=>setTab('create')}>Create First Client Account</Btn>
            </div>
          )}
          {clients.map(c => (
            <div key={c.id} style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:'16px 20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:`${GOLD}22`, border:`1px solid ${GOLD}44`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:GOLD, flexShrink:0 }}>
                  {(c.client||'?').slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:NAVY }}>{c.client || '—'}</div>
                  <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{c.client_email}</div>
                  <div style={{ fontSize:11, color:'#AAA', marginTop:2 }}>Matter: {c.name} · Deadline: {fmt(c.deadline)}</div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  {editId===c.id ? (
                    <>
                      <Input value={editEmail} onChange={e=>setEditEmail(e.target.value)} placeholder="New email" style={{ width:200, fontSize:12, padding:'5px 8px' }} />
                      <Btn onClick={()=>handleUpdateEmail(c.id)} style={{ padding:'6px 14px', fontSize:12 }}>Save</Btn>
                      <Btn variant="ghost" onClick={()=>setEditId(null)} style={{ padding:'6px 12px', fontSize:12 }}>Cancel</Btn>
                    </>
                  ) : (
                    <>
                      {isAdmin && <button onClick={()=>{ setEditId(c.id); setEditEmail(c.client_email); }} style={{ background:'none', border:'1px solid #D0CBBF', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', color:NAVY, fontFamily:'inherit' }}>Edit Email</button>}
                      {isAdmin && <button onClick={()=>handleRemoveAccess(c.id)} style={{ background:'none', border:'1px solid #F5C0B8', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', color:'#C0392B', fontFamily:'inherit' }}>Remove Access</button>}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create new client */}
      {tab==='create' && (
        <div style={{ maxWidth:600 }}>
          <Card title="Create Client Portal Account">
            <div style={{ fontSize:13, color:'#888', marginBottom:20, lineHeight:1.7 }}>
              This creates a login for your client and links it to their matter. They will see only their own matter, tasks, timeline, documents and messages when they sign in as a Client.
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <Field label="Client Name">
                <Input placeholder="e.g. AgaOne" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
              </Field>
              <Field label="Link to Matter">
                <Select value={form.matter_id} onChange={e=>setForm({...form,matter_id:e.target.value})}>
                  <option value="">Select a matter…</option>
                  {unlinkedMatters.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                  {unlinkedMatters.length===0 && <option disabled>All matters already have client access</option>}
                </Select>
              </Field>
              <Field label="Client Email">
                <Input type="email" placeholder="client@company.com" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} />
              </Field>
              <Field label="Temporary Password">
                <Input type="password" placeholder="Min. 6 characters" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} />
              </Field>
            </div>

            {error   && <div style={{ marginTop:14, background:'#FDF0EE', border:'1px solid #F5C0B8', borderRadius:6, padding:'10px 14px', fontSize:13, color:'#C0392B', lineHeight:1.5 }}>{error}</div>}
            {success && (
              <div style={{ marginTop:14, background:'#EAF7EE', border:'1px solid #A8DDB8', borderRadius:8, padding:'14px 16px' }}>
                <div style={{ fontSize:13, color:'#27AE60', fontWeight:600, marginBottom:4 }}>Account created successfully</div>
                <div style={{ fontSize:12, color:'#2E7D52', lineHeight:1.7 }}>{success}</div>
                <div style={{ fontSize:12, color:'#2E7D52', marginTop:8, padding:'8px 12px', background:'#D4EDDA', borderRadius:6 }}>
                  Share with client:<br/>
                  URL: <strong>app.kolumanlaw.com</strong><br/>
                  Login type: <strong>Client</strong><br/>
                  Email: <strong>{form.email || '(see above)'}</strong><br/>
                  Password: Send separately via WhatsApp or secure email
                </div>
              </div>
            )}

            <div style={{ marginTop:18 }}>
              <Btn onClick={handleCreate} style={{ opacity:valid&&!saving?1:0.5 }}>
                {saving ? 'Creating account…' : 'Create Client Account'}
              </Btn>
            </div>
          </Card>

          {/* Instructions */}
          <div style={{ background:'#FFF8EE', border:'1px solid #F5E4B8', borderRadius:8, padding:'16px 18px', fontSize:13, color:'#7A5C1E', lineHeight:1.8, marginTop:16 }}>
            <strong>How to share access with your client:</strong><br/>
            1. Create the account above<br/>
            2. Send them the URL: <strong>app.kolumanlaw.com</strong><br/>
            3. Tell them to click <strong>"Client"</strong> on the login screen<br/>
            4. Send their email and password <strong>separately</strong> (e.g. email + WhatsApp)<br/>
            5. To change their password later: go to <strong>Supabase → Authentication → Users</strong>
          </div>
        </div>
      )}
    </div>
  );
}
